/*
 * a11y-tables.js — accessibility repairs for the DataTables-driven spec tables.
 * AudioEye Aug 2026: SEAB-50, SEAB-61, BULL-66, BULL-72, TATU-93, TATU-95, SALT-100.
 *
 * The spec tables are built in several places (Liquid in tags-specs, JS payloads
 * in metafields-specs / catalogue-table / saltiga-landing-specification) and are
 * then handed to DataTables. Rather than edit each site, this watches for tables
 * appearing and layers the fixes on once:
 *
 *   1. DataTables stamps role="grid" on every table it initialises. These are
 *      static data tables — paging, ordering and info are all off — so the grid
 *      role only misleads assistive tech about the keyboard model. Removed, which
 *      restores native table semantics. (SEAB-50, BULL-66)
 *   2. Adds a <caption> where one is missing, taken from the nearest preceding
 *      heading. Visually hidden, so the accessible name lands with no visual
 *      change; drop the class to make it visible. (BULL-72, TATU-95, SALT-100)
 *   3. Names the per-column filter selects from their data-header, so their
 *      relationship to the column is conveyed by more than visual placement.
 *      (SEAB-61)
 *   4. Announces the row count after filtering through a polite live region,
 *      since DataTables runs with info:false and the table silently rewrites
 *      itself on change. (TATU-93)
 *
 * Vanilla JS, no dependencies. Loaded once site-wide.
 */
(function () {
  'use strict';

  var TABLES = 'table.spec-table, table.rwd-table, table.feat-table';

  function textOf(el) {
    return el && el.textContent ? el.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  /* Closest heading *before* this element in document order, walking outward. */
  function nearestHeading(el) {
    var node = el;
    while (node && node !== document.body) {
      var prev = node.previousElementSibling;
      while (prev) {
        if (/^H[1-4]$/.test(prev.tagName) && textOf(prev)) return textOf(prev);
        var inner = prev.querySelectorAll ? prev.querySelectorAll('h1,h2,h3,h4') : [];
        if (inner.length) {
          var last = textOf(inner[inner.length - 1]);
          if (last) return last;
        }
        prev = prev.previousElementSibling;
      }
      node = node.parentElement;
    }
    return '';
  }

  function captionFor(table) {
    var explicit = table.getAttribute('data-caption');
    if (explicit) return explicit;
    var heading = nearestHeading(table);
    if (heading) return heading.length > 120 ? heading.slice(0, 117) + '…' : heading;
    var h1 = textOf(document.querySelector('h1'));
    return h1 ? h1 + ' specifications' : 'Specifications';
  }

  /* Live region paired with a table, created on demand. */
  function liveRegionFor(table) {
    var host = table.parentNode;
    if (!host) return null;
    var region = host.querySelector(':scope > .a11y-table-status');
    if (!region) {
      region = document.createElement('p');
      region.className = 'a11y-table-status visually-hidden';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      host.appendChild(region);
    }
    return region;
  }

  function visibleRows(table) {
    var body = table.tBodies && table.tBodies[0];
    if (!body) return 0;
    return Array.prototype.filter.call(body.rows, function (r) {
      return r.offsetParent !== null;
    }).length;
  }

  function announce(table) {
    var region = liveRegionFor(table);
    if (!region) return;
    var n = visibleRows(table);
    region.textContent = n === 1 ? '1 result shown' : n + ' results shown';
  }

  function enhanceTable(table) {
    if (table.dataset.a11yTable === '1') return;
    table.dataset.a11yTable = '1';

    // 1. Static data table, not a grid.
    if (table.getAttribute('role') === 'grid') table.removeAttribute('role');

    // 2. Accessible name via a real caption.
    if (!table.querySelector('caption')) {
      var caption = document.createElement('caption');
      caption.className = 'visually-hidden';
      caption.textContent = captionFor(table);
      table.insertBefore(caption, table.firstChild);
    }

    // 3. Create the live region now, empty. A region that is inserted and
    //    populated in the same tick is usually missed by screen readers, so it
    //    has to be in the DOM and observed before the first filter change.
    liveRegionFor(table);
  }

  /* DataTables can stamp role="grid" after we have already seen the table. */
  function stripGridRoles() {
    var grids = document.querySelectorAll('table[role="grid"]');
    for (var i = 0; i < grids.length; i++) grids[i].removeAttribute('role');
  }

  function labelFilter(select) {
    if (select.dataset.a11yFilter === '1') return;
    select.dataset.a11yFilter = '1';
    if (!select.getAttribute('aria-label')) {
      var header = select.getAttribute('data-header');
      if (!header && select.classList.contains('filter-by-model')) header = 'model';
      header = (header || '').replace(/[+_]/g, ' ').trim().toLowerCase();
      select.setAttribute('aria-label', header ? 'Filter by ' + header : 'Filter table');
    }
    // 4. Announce the new row count once the table has redrawn. Climb from the
    //    select to the nearest ancestor that actually contains a spec table, so
    //    pages carrying several tables announce the one this filter drives.
    select.addEventListener('change', function () {
      var table = null;
      var node = select.parentElement;
      while (node && !table) {
        table = node.querySelector(TABLES);
        node = node.parentElement;
      }
      if (table) setTimeout(function () { announce(table); }, 250);
    });
  }

  function sweep() {
    stripGridRoles();
    var tables = document.querySelectorAll(TABLES);
    for (var i = 0; i < tables.length; i++) enhanceTable(tables[i]);
    var filters = document.querySelectorAll('select.desktop-filter, select.filter-by-model');
    for (var j = 0; j < filters.length; j++) labelFilter(filters[j]);
  }

  function start() {
    sweep();
    if (!('MutationObserver' in window)) return;
    var queued = false;
    var obs = new MutationObserver(function () {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(function () { queued = false; sweep(); });
    });
    obs.observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['role']
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
